"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { X } from "lucide-react";
import { Button } from "@ui/components/button";
import { Badge } from "@ui/components/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@ui/components/select";
import { propertyQueryKey } from "@saas/properties/lib/api";
import { usePropertyPageGroupsQuery } from "@saas/properties/lib/page-groups-api";
import { Skeleton } from "@ui/components/skeleton";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@shared/lib/api-client";
import { DateRangeFilter, TimeRangeType, DateRange } from "@saas/shared/components/DateRangeFilter";
import { type PageIntent } from "@repo/core/client";
import { PageDropdown } from "@saas/properties/components/PageDropdown";

interface AnalyticsFiltersProps {
  propertyId: string;
  propertyApiKey?: string; // Optional: use this directly for marketing/demo components
  timeRange: TimeRangeType;
  onTimeRangeChange: (value: TimeRangeType) => void;
  customDateRange?: DateRange;
  onCustomDateRangeChange: (value: DateRange | undefined) => void;
  selectedPagePath?: string;
  onPagePathChange: (value: string | undefined) => void;
  selectedIntents?: PageIntent[];
  onIntentsChange?: (value: PageIntent[]) => void;
  selectedCategory?: string;
  onCategoryChange?: (value: string | undefined) => void;
  selectedSubcategory?: string;
  onSubcategoryChange?: (value: string | undefined) => void;
  selectedCountry?: string;
  onCountryChange?: (value: string | undefined) => void;
  selectedRegion?: string;
  onRegionChange?: (value: string | undefined) => void;
  selectedCity?: string;
  onCityChange?: (value: string | undefined) => void;
  selectedAiModel?: string;
  onAiModelChange?: (value: string | undefined) => void;
  aiModelOptions?: Array<{ id: string; name: string; company?: string }>;
  selectedGroupId?: string;
  onGroupIdChange?: (value: string | undefined) => void;
  isLoading?: boolean;
  isLive?: boolean;
}

export function AnalyticsFilters({
  propertyId,
  propertyApiKey,
  timeRange,
  onTimeRangeChange,
  customDateRange,
  onCustomDateRangeChange,
  selectedPagePath,
  onPagePathChange,
  selectedIntents = [],
  onIntentsChange,
  selectedCategory,
  onCategoryChange,
  selectedSubcategory,
  onSubcategoryChange,
  selectedCountry,
  onCountryChange,
  selectedRegion,
  onRegionChange,
  selectedCity,
  onCityChange,
  selectedAiModel,
  onAiModelChange,
  aiModelOptions,
  selectedGroupId,
  onGroupIdChange,
  isLoading = false,
  isLive = false,
}: AnalyticsFiltersProps) {
  const t = useTranslations("properties.analytics");
  const { data: pageGroups } = usePropertyPageGroupsQuery(propertyId);
  const showPageGroupFilter = !!onGroupIdChange && (pageGroups?.length ?? 0) > 0;
  
  // Track the selected page title for display (set when user selects a page from dropdown)
  const [selectedPageTitle, setSelectedPageTitle] = useState<string | undefined>();
  
  // Get property to resolve propertyId to propertyApiKey (only if propertyApiKey not provided)
  const { data: property } = useQuery({
    queryKey: propertyQueryKey(propertyId),
    queryFn: async () => {
      const response = await apiClient.properties[":id"].$get({
        param: { id: propertyId },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch property");
      }

      const data = await response.json() as any;
      return {
        ...data,
        createdAt: new Date(data.createdAt),
        updatedAt: new Date(data.updatedAt),
        verifiedAt: data.verifiedAt ? new Date(data.verifiedAt) : null,
      };
    },
    enabled: !propertyApiKey && !!propertyId, // Only fetch if propertyApiKey is not provided AND propertyId exists
  });
  
  // Use provided propertyApiKey or fallback to resolved one
  const effectiveApiKey = propertyApiKey || property?.apiKey || "";
  
  // Clear page selection
  const handleClearPage = () => {
    onPagePathChange(undefined);
    setSelectedPageTitle(undefined);
  };
  
  // Handle page selection with title tracking
  const handlePageSelect = (path: string) => {
    if (path) {
      onPagePathChange(path);
      // Note: The PageDropdown will show the path, but we'll update the badge display
      setSelectedPageTitle(path); // Use path as display until we have title
    } else {
      onPagePathChange(undefined);
      setSelectedPageTitle(undefined);
    }
  };

  // Handle intent selection
  const handleToggleIntent = (intent: PageIntent) => {
    if (!onIntentsChange) return;
    
    if (selectedIntents.includes(intent)) {
      onIntentsChange(selectedIntents.filter(i => i !== intent));
    } else {
      onIntentsChange([...selectedIntents, intent]);
    }
  };

  // Clear intent selection
  const handleClearIntent = (intent: PageIntent) => {
    if (!onIntentsChange) return;
    onIntentsChange(selectedIntents.filter(i => i !== intent));
  };

  // Clear all intents
  const handleClearAllIntents = () => {
    if (!onIntentsChange) return;
    onIntentsChange([]);
  };
  
  // Handle AI model selection
  const handleAiModelSelect = (modelId: string) => {
    if (onAiModelChange) {
      onAiModelChange(modelId === "all" ? undefined : modelId);
    }
  };
  
  // Clear AI model selection
  const handleClearAiModel = () => {
    if (onAiModelChange) {
      onAiModelChange(undefined);
    }
  };
  
  // Clear category selection
  const handleClearCategory = () => {
    if (onCategoryChange) {
      onCategoryChange(undefined);
      // When clearing a category, also clear subcategory
      if (onSubcategoryChange) {
        onSubcategoryChange(undefined);
      }
    }
  };
  
  // Clear subcategory selection
  const handleClearSubcategory = () => {
    if (onSubcategoryChange) {
      onSubcategoryChange(undefined);
    }
  };
  
  // Clear location filters
  const handleClearCountry = () => {
    if (onCountryChange) {
      onCountryChange(undefined);
      // When clearing a country, also clear region and city
      if (onRegionChange) onRegionChange(undefined);
      if (onCityChange) onCityChange(undefined);
    }
  };
  
  const handleClearRegion = () => {
    if (onRegionChange) {
      onRegionChange(undefined);
      // When clearing a region, also clear city
      if (onCityChange) onCityChange(undefined);
    }
  };
  
  const handleClearCity = () => {
    if (onCityChange) {
      onCityChange(undefined);
    }
  };
  
  const handleClearGroup = () => {
    if (onGroupIdChange) onGroupIdChange(undefined);
  };

  // Clear all filters
  const handleClearAllFilters = () => {
    onPagePathChange(undefined);
    setSelectedPageTitle(undefined);
    if (onIntentsChange) onIntentsChange([]);
    if (onCategoryChange) onCategoryChange(undefined);
    if (onSubcategoryChange) onSubcategoryChange(undefined);
    if (onCountryChange) onCountryChange(undefined);
    if (onRegionChange) onRegionChange(undefined);
    if (onCityChange) onCityChange(undefined);
    if (onAiModelChange) onAiModelChange(undefined);
    if (onGroupIdChange) onGroupIdChange(undefined);
  };
  
  // Get selected page title for display
  const getSelectedPageTitle = () => {
    if (!selectedPagePath) return t("allPages");
    return selectedPageTitle || selectedPagePath;
  };
  
  // Get selected AI model name
  const getSelectedAiModelName = () => {
    if (!selectedAiModel || !aiModelOptions) return "All AI Models";
    const selectedModel = aiModelOptions.find(model => model.id === selectedAiModel);
    return selectedModel ? (selectedModel.company || selectedModel.name) : "All AI Models";
  };

  // Get intent display name
  const getIntentDisplayName = (intent: PageIntent): string => {
    // Try to get translation, fallback to the intent name itself
    try {
      return t(`intents.${intent.toLowerCase()}` as any);
    } catch {
      return intent;
    }
  };
  
  const selectedGroupName = pageGroups?.find((g) => g.id === selectedGroupId)?.name;

  // Check if any filters are active
  const hasActiveFilters = selectedPagePath || selectedIntents.length > 0 || selectedCategory || selectedSubcategory ||
                          selectedCountry || selectedRegion || selectedCity || selectedAiModel || selectedGroupId;
  
  return (
    <div className="flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between sm:space-x-2">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        {/* Time range selector using the DateRangeFilter component */}
        <DateRangeFilter
          timeRange={timeRange}
          onTimeRangeChange={onTimeRangeChange}
          customDateRange={customDateRange}
          onCustomDateRangeChange={onCustomDateRangeChange}
          isLoading={isLoading}
          isLive={isLive}
          showLiveOption={true}
          translationNamespace="properties.analytics"
        />
        
        {/* Page filter with intent filtering - now uses lazy loading */}
        <div className="flex items-center gap-2">
          {!effectiveApiKey ? (
            <Skeleton className="h-9 w-[200px]" />
          ) : (
            <PageDropdown
              propertyApiKey={effectiveApiKey}
              selectedPage={selectedPagePath}
              selectedPageTitle={selectedPageTitle}
              selectedIntents={selectedIntents}
              onPageSelect={handlePageSelect}
              onIntentsChange={(intents) => onIntentsChange?.(intents)}
              placeholder={t("allPages")}
              className="w-[200px]"
            />
          )}
        </div>
        
        {/* AI Model filter - only show if aiModelOptions is provided */}
        {aiModelOptions && onAiModelChange && (
          <div className="flex items-center gap-2">
            <Select
              value={selectedAiModel || "all"}
              onValueChange={handleAiModelSelect}
              disabled={isLoading}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All AI Models">
                  {getSelectedAiModelName()}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All AI Models</SelectItem>
                {aiModelOptions.map((model) => (
                  <SelectItem key={model.id} value={model.id}>
                    {model.company || model.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        {/* Page group filter - only show when onGroupIdChange provided and property has groups */}
        {showPageGroupFilter && (
          <div className="flex items-center gap-2">
            <Select
              value={selectedGroupId ?? "all"}
              onValueChange={(v) => onGroupIdChange(v === "all" ? undefined : v)}
              disabled={isLoading}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All pages" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All pages</SelectItem>
                {pageGroups?.map((g) => (
                  <SelectItem key={g.id} value={g.id}>
                    {g.name} ({(g._count?.pages ?? 0)})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
      
      {/* Active filters */}
      <div className="flex flex-wrap items-center gap-2">
        {hasActiveFilters && (
          <>
            {selectedPagePath && (
              <Badge status="info" className="flex items-center gap-1">
                <span>Page: {getSelectedPageTitle()}</span>
                <X className="h-3 w-3 cursor-pointer" onClick={handleClearPage} />
              </Badge>
            )}
            
            {selectedIntents.map((intent) => (
              <Badge key={intent} status="info" className="flex items-center gap-1">
                <span>Intent: {getIntentDisplayName(intent)}</span>
                <X className="h-3 w-3 cursor-pointer" onClick={() => handleClearIntent(intent)} />
              </Badge>
            ))}
            
            {selectedAiModel && (
              <Badge status="info" className="flex items-center gap-1">
                <span>AI Model: {getSelectedAiModelName()}</span>
                <X className="h-3 w-3 cursor-pointer" onClick={handleClearAiModel} />
              </Badge>
            )}
            
            {selectedCategory && (
              <Badge status="info" className="flex items-center gap-1">
                <span>Category: {selectedCategory}</span>
                <X className="h-3 w-3 cursor-pointer" onClick={handleClearCategory} />
              </Badge>
            )}
            
            {selectedSubcategory && (
              <Badge status="info" className="flex items-center gap-1">
                <span>Subcategory: {selectedSubcategory}</span>
                <X className="h-3 w-3 cursor-pointer" onClick={handleClearSubcategory} />
              </Badge>
            )}
            
            {selectedCountry && (
              <Badge status="info" className="flex items-center gap-1">
                <span>Country: {selectedCountry}</span>
                <X className="h-3 w-3 cursor-pointer" onClick={handleClearCountry} />
              </Badge>
            )}
            
            {selectedRegion && (
              <Badge status="info" className="flex items-center gap-1">
                <span>Region: {selectedRegion}</span>
                <X className="h-3 w-3 cursor-pointer" onClick={handleClearRegion} />
              </Badge>
            )}
            
            {selectedCity && (
              <Badge status="info" className="flex items-center gap-1">
                <span>City: {selectedCity}</span>
                <X className="h-3 w-3 cursor-pointer" onClick={handleClearCity} />
              </Badge>
            )}
            {selectedGroupId && selectedGroupName && (
              <Badge status="info" className="flex items-center gap-1">
                <span>Group: {selectedGroupName}</span>
                <X className="h-3 w-3 cursor-pointer" onClick={handleClearGroup} />
              </Badge>
            )}
            
            {/* Clear all filters button */}
            {hasActiveFilters && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleClearAllFilters}
                className="h-7 text-xs ml-2"
              >
                Clear All
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
} 